import { TFunction } from 'i18next'
import { Device, Emc, Storage } from '../../installation/forms'
import { ControlledDto, ControllerDto, DeviceLinkDto } from '../../installation/api'
import { getUuid } from '../../utils/get-uuid'
import { logError } from '../../utils/logger'
import { getDeviceIsPaired } from './get-device-is-paired'
import { getEmcPairingId, getEvsePairingId, getIoPairingId, getStoragePairingId } from './get-device-pairing-id'
import { getEmcSerialNumber, getEvseSerialNumber, getIoSerialNumber, getStorageSerialNumber } from './get-device-serial-number'
import { getDeviceStatus } from './get-device-status'

/** EMC */
export const parseEmcDeviceLink = (deviceLink: Nullable<DeviceLinkDto>): Emc => {
  return {
    id: getUuid(),
    isNew: false,
    deviceId: deviceLink.id?.toString() ?? '',
    navigationId: deviceLink.id?.toString() ?? '',
    deviceCategory: 'EMC',
    name: 'EMC',
    pairingId: getEmcPairingId(deviceLink),
    isPaired: getDeviceIsPaired(deviceLink.pairingState),
    initialPairingId: getEmcPairingId(deviceLink),
    serialNumber: getEmcSerialNumber(deviceLink),
    deviceStatus: getDeviceStatus(deviceLink.pairingState, deviceLink.deviceStatus),
    deviceStatusCodeBits: deviceLink.deviceStatusCode,
    emcHardwareDeviceId: deviceLink.deviceId,
  }
}

/** STORAGE */
export const parseStorageDeviceLink = (deviceLink: Nullable<DeviceLinkDto>, t: TFunction): Storage => {
  return {
    id: getUuid(),
    isNew: false,
    deviceId: deviceLink.id?.toString() ?? '',
    navigationId: deviceLink.id?.toString() ?? '',
    deviceCategory: 'Storage',
    name: deviceLink.deviceName ?? t('installation.types.storage'),
    pairingId: getStoragePairingId(deviceLink),
    initialPairingId: getStoragePairingId(deviceLink),
    serialNumber: getStorageSerialNumber(deviceLink),
    deviceStatus: getDeviceStatus(deviceLink.pairingState, deviceLink.deviceStatus),
    isPaired: getDeviceIsPaired(deviceLink.pairingState),
    deviceStatusCodeBits: deviceLink.deviceStatusCode,
    deviceType: getStorageType(deviceLink),
    storageHardwareDeviceId: deviceLink.deviceId || null,
  }
}

export const getStorageType = (storage: Nullable<DeviceLinkDto>): Storage['deviceType'] => {
  if (!storage || !storage.deviceId) return null
  return getStorageTypeFromPairingId(storage.deviceId)
}

export const getStorageTypeFromPairingId = (pairingId: string): Storage['deviceType'] => {
  if (pairingId.startsWith('XEM-31') || pairingId.startsWith('31')) return 'XEM - R1'
  if (pairingId.startsWith('33')) return 'XCM - R2'
  return null
}

/** CONTROLLED, EVSE */
export const parseControlledDevice = (evse: Nullable<ControlledDto>): Device => {
  return {
    id: getUuid(),
    isNew: false,
    deviceId: `${evse.id ?? ''}`,
    navigationId: `${evse.id ?? ''}`,
    name: evse.deviceName ?? '',
    deviceCategory: getEvseType(evse),
    pairingId: getEvsePairingId(evse),
    serialNumber: getEvseSerialNumber(evse),
    deviceStatus: getDeviceStatus(evse.pairingState, evse.deviceStatus),
    isPaired: getDeviceIsPaired(evse.pairingState),
    deviceStatusCodeBits: evse.deviceStatusCode,
  }
}
const getEvseType = (evse: Nullable<ControlledDto>): 'ModbusTCP Evse' | 'EEBus Evse' => {
  if (evse.media === 'ModbusTCP') return 'ModbusTCP Evse'
  if (evse.media === 'EEBus') return 'EEBus Evse'
  logError('Type of controlled device could not be parsed. ModbusTCP Evse was returned as a fallback.')
  return 'ModbusTCP Evse'
}

/** CONTROLLERS */
export const parseControllerDevice = (ioInterface: Nullable<ControllerDto>): Device => {
  return {
    id: getUuid(),
    isNew: false,
    deviceId: `${ioInterface.id ?? ''}`,
    navigationId: `${ioInterface.id ?? ''}`,
    name: ioInterface.deviceName ?? '',
    deviceCategory: getControllerCategory(ioInterface),
    pairingId: getIoPairingId(ioInterface),
    serialNumber: getIoSerialNumber(ioInterface),
    deviceStatus: getDeviceStatus(ioInterface.pairingState, ioInterface.deviceStatus),
    isPaired: getDeviceIsPaired(ioInterface.pairingState),
    deviceStatusCodeBits: ioInterface.deviceStatusCode,
  }
}

const getControllerCategory = (ioInterface: Nullable<ControllerDto>): 'Heatpump' | 'HTC320H I/O' | 'Storage I/O' | 'MyPV I/O' => {
  if (ioInterface.type === 'HpWithISG') return 'Heatpump'

  if (ioInterface.type === 'MyPvHeater') return 'MyPV I/O'

  if (ioInterface.type === 'IOController') {
    if (ioInterface.media === 'ModbusRTU') return 'HTC320H I/O'
    if (ioInterface.media === 'Storage') return 'Storage I/O'
  }

  logError('Type of controller could not be parsed. HTC320H I/O was returned as a fallback.')
  return 'HTC320H I/O'
}
